<template>
  <div class="dynamic-component-viewer">
    <div
      v-if="isLoading"
      class="loader-wrapper"
    >
      <tiny-loader />
    </div>
    <div v-else class="page-content" ref="pageContent" :style="pageContentStyle">
      <div
        v-for="(page, index) in componentPages"
        v-show="index === currentPageIndex"
        :key="index"
        class="page-container"
        :style="pageContainerStyle(page)"
      >
        <div class="background" :style="pageBackgroundStyle(page)"></div>
        <viewer-widgets
          v-if="page.content.widgets"
          :widgets="page.content.widgets"
          :state-variables="getStateVariables()"
          :is-visible="index === currentPageIndex"
          @userInteraction="onUserInteraction"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Segment from '@/Segment';
import BaseUI from '@riseupil/base-ui';
import storyLib from '@riseupil/story-lib';
import consts from '@riseupil/story-lib/src/components/consts';
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  name: 'DynamicComponentViewer',
  components: {
    TinyLoader: BaseUI.TinyLoader,
    ViewerWidgets: storyLib.ViewerWidgets,
  },
  props: {
    componentName: {
      type: String,
      required: true,
    },
    analyticsPrefix: {
      type: String,
      default: '',
    },
    extraParams: {
      type: Object,
      default: () => ({}),
    },
    isUnauthenticated: {
      type: Boolean,
      default: false,
    },
    mountOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      componentPages: [],
      currentPageIndex: 0,
      containerHeight: 0,
      containerWidth: 0,
    };
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  async mounted() {
    await this.loadComponent(this.extraParams, this.isUnauthenticated);
    if (this.mountOptions.currentPageIndex) {
      this.currentPageIndex = this.mountOptions.currentPageIndex;
    } else {
      this.trackPageView(this.currentPageIndex, undefined);
    }
    this.$nextTick(() => {
      this.onResize();
      window.addEventListener('resize', this.onResize);
    });
  },
  computed: {
    ...mapGetters('dynamicComponent', [
      'getComponentByName',
      'isComponentLoading',
      'getComponentError',
      'hasComponent',
      'getComponentStateVariables',
      'getStateVariableByName',
    ]),
    ...mapState('genericError', ['ERROR_TYPES']),
    component() {
      return this.getComponentByName(this.componentName);
    },
    isLoading() {
      return this.isComponentLoading(this.componentName);
    },
    currentPage() {
      return this.componentPages[this.currentPageIndex];
    },
    pageHeight() {
      if (!this.currentPage || !this.currentPage.ratio) {
        return consts.BASE_SCREEN_HEIGHT;
      }
      const { ratio } = this.currentPage;
      return parseInt(ratio.replace('px', ''), 10) || consts.BASE_SCREEN_HEIGHT;
    },
    pageContentStyle() {
      if (!this.currentPage || !this.containerWidth) {
        return {};
      }

      const pageHeight = parseInt(this.currentPage.ratio?.replace('px', ''), 10) || consts.BASE_SCREEN_HEIGHT;
      const pageWidth = consts.BASE_SCREEN_WIDTH;
      const scale = this.containerWidth / pageWidth;
      const scaledHeight = pageHeight * scale;

      return {
        height: `${scaledHeight}px`,
      };
    },
  },
  watch: {
    currentPageIndex(newIndex, oldIndex) {
      if (newIndex !== oldIndex) {
        this.trackPageView(newIndex, oldIndex);
      }
    },
  },
  methods: {
    ...mapActions('dynamicComponent', ['fetchDynamicComponent', 'setStateVariableByName']),
    ...mapActions('genericError', ['setErrorType']),
    trackPageView(pageIndex, previousPageIndex) {
      if (this.componentPages[pageIndex]) {
        Segment.trackUserGot(`${this.analyticsPrefix}pageViewed`, {
          componentName: this.componentName,
          pageIndex,
          pageName: this.componentPages[pageIndex].name,
          totalPages: this.componentPages.length,
          previousPageIndex,
        });
      }
    },
    // Expose helpers to consumers
    getStateVariables() {
      return this.getComponentStateVariables(this.componentName);
    },
    getStateVariableByNameLocal(variableName) {
      return this.getStateVariableByName({ componentName: this.componentName, variableName });
    },
    setStateVariableByNameLocal(variableName, value) {
      this.setStateVariableByName({ componentName: this.componentName, variableName, value });
    },
    onResize() {
      if (this.$refs.pageContent) {
        this.containerHeight = this.$refs.pageContent.clientHeight;
        this.containerWidth = this.$refs.pageContent.clientWidth;
      }
    },

    pageContainerStyle(page) {
      const pageHeight = parseInt(page.ratio?.replace('px', ''), 10) || consts.BASE_SCREEN_HEIGHT;
      const pageWidth = consts.BASE_SCREEN_WIDTH;

      const scale = this.containerWidth / pageWidth;
      return {
        width: `${pageWidth * scale}px`,
        height: `${pageHeight * scale}px`,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      };
    },

    pageBackgroundStyle(page) {
      const pageHeight = parseInt(page.ratio?.replace('px', ''), 10) || consts.BASE_SCREEN_HEIGHT;
      const pageWidth = consts.BASE_SCREEN_WIDTH;

      return {
        background: page.content.background || '#FFFFFF',
        width: `${pageWidth}px`,
        height: `${pageHeight}px`,
      };
    },

    async onUserInteraction(data) {
      if (data.widgetType === 'text' || data.eventName === 'nextPage' || data.eventName === 'previousPage') {
        Segment.trackUserGot(`${this.analyticsPrefix}buttonClicked`, {
          componentName: this.componentName,
          pageIndex: this.currentPageIndex,
          buttonText: data.text,
        });
        if (data.eventName === 'nextPage') {
          this.nextPage();
        }
        if (data.eventName === 'previousPage') {
          this.previousPage();
        }
      }

      if (data && data.eventName === 'StoryExternalLinkClicked' && data.url) {
        Segment.trackUserGot(`${this.analyticsPrefix}_linkClicked`, {
          componentName: this.componentName,
          pageIndex: this.currentPageIndex,
          link: data.url,
        });
        window.open(data.url, '_blank');
      }

      if (data && data.eventName === 'InputValueChanged' && data.variableName) {
        Segment.trackUserGot(`${this.analyticsPrefix}buttonClicked`, {
          componentName: this.componentName,
          ...data,
          buttonText: data.text,
        });
        this.setStateVariableByNameLocal(data.variableName, data.value);
        if (data.nextPage) {
          this.nextPage();
        }
      }

      this.$emit('userInteraction', data);
    },

    nextPage() {
      if (this.currentPageIndex < this.componentPages.length - 1) {
        const previousIndex = this.currentPageIndex;
        this.currentPageIndex += 1;

        this.$emit('page-changed', {
          previousIndex,
          currentIndex: this.currentPageIndex,
          page: this.componentPages[this.currentPageIndex],
          isLastPage: this.currentPageIndex === this.componentPages.length - 1,
        });
      } else {
        this.$emit('navigation-complete', {
          componentName: this.componentName,
          totalPages: this.componentPages.length,
        });
      }
    },

    previousPage() {
      if (this.currentPageIndex > 0) {
        const previousIndex = this.currentPageIndex;
        this.currentPageIndex -= 1;

        this.$emit('page-changed', {
          previousIndex,
          currentIndex: this.currentPageIndex,
          page: this.componentPages[this.currentPageIndex],
          isFirstPage: this.currentPageIndex === 0,
        });
      }
    },

    goToPage(pageIndex) {
      if (pageIndex >= 0 && pageIndex < this.componentPages.length) {
        const previousIndex = this.currentPageIndex;
        this.currentPageIndex = pageIndex;

        this.$emit('page-changed', {
          previousIndex,
          currentIndex: this.currentPageIndex,
          page: this.componentPages[this.currentPageIndex],
          isFirstPage: this.currentPageIndex === 0,
          isLastPage: this.currentPageIndex === this.componentPages.length - 1,
        });
      }
    },

    getCurrentPage() {
      return this.componentPages[this.currentPageIndex];
    },

    getTotalPages() {
      return this.componentPages.length;
    },

    async reloadComponent(newExtraParams = {}) {
      const updatedExtraParams = { ...this.extraParams, ...newExtraParams };
      await this.loadComponent(updatedExtraParams);
      if (this.mountOptions.currentPageIndex) {
        this.currentPageIndex = this.mountOptions.currentPageIndex;
      }
      this.$nextTick(() => {
        this.onResize();
      });
    },

    async loadComponent(extraParams = {}, isUnauthenticated = false) {
      try {
        const component = await this.fetchDynamicComponent({
          componentName: this.componentName,
          extraParams,
          isUnauthenticated,
        });
        if (component && component.pages) {
          this.componentPages = component.pages;
          this.currentPageIndex = 0;

          this.$emit('component-loaded', {
            component,
            totalPages: this.componentPages.length,
          });

          this.$nextTick(() => {
            this.onResize();
          });
        }
      } catch (error) {
        this.setErrorType(this.ERROR_TYPES.SERVER_ERROR);
        this.$emit('component-error', {
          error,
          componentName: this.componentName,
        });

        console.error('Component load failed:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dynamic-component-viewer {
  display: block;
  width: 100%;

  .loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;

    .loader {
      font-size: 16px;
      color: #666;
    }
  }

  .page-content {
    width: 100%;
    display: flex;
    justify-content: center;
    overflow: hidden;
  }

    .page-container {
    position: relative;
    display: block;

    .background {
      position: absolute;
      top: 0;
      left: 0;
    }
  }

}
</style>
